import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { DollarSign, Trash2, Upload, Loader2, FileSpreadsheet, CheckCircle, AlertCircle, Percent, Receipt } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Finance = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('discounts');
    const [discounts, setDiscounts] = useState([]);
    const [debts, setDebts] = useState([]);
    const [parents, setParents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const discountFileRef = useRef(null);
    const debtFileRef = useRef(null);

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [discRes, debtRes, parRes] = await Promise.all([
                axios.get(`${API_URL}/api/discounts`, { withCredentials: true }),
                axios.get(`${API_URL}/api/debts`, { withCredentials: true }),
                axios.get(`${API_URL}/api/parents`, { withCredentials: true })
            ]);
            setDiscounts(discRes.data);
            setDebts(debtRes.data);
            setParents(parRes.data);
        } catch { toast.error('Failed to load data'); }
        finally { setLoading(false); }
    };

    const getParentInfo = (parentId) => {
        const p = parents.find(p => p.id === parentId);
        return p ? { name: `${p.first_name} ${p.last_name}`, code: p.reg_code } : { name: 'Unknown', code: '—' };
    };

    const handleUpload = async (type, e) => {
        const file = e.target.files[0]; if (!file) return;
        setUploading(true); setUploadResult(null);
        const fd = new FormData(); fd.append('file', file);
        try {
            const res = await axios.post(`${API_URL}/api/${type}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            setUploadResult(res.data);
            if (res.data.created > 0) { toast.success(`${res.data.created} record(s) imported`); fetchData(); }
        } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
        finally { setUploading(false); if (discountFileRef.current) discountFileRef.current.value = ''; if (debtFileRef.current) debtFileRef.current.value = ''; }
    };

    const handleDeleteOne = async (type, id) => {
        if (!window.confirm('Delete this record?')) return;
        try { await axios.delete(`${API_URL}/api/${type}/${id}`, { withCredentials: true }); toast.success('Deleted'); fetchData(); }
        catch { toast.error('Failed to delete'); }
    };

    const handleDeleteAll = async (type) => {
        if (!window.confirm(`Delete ALL ${type}? This cannot be undone!`)) return;
        try { await axios.delete(`${API_URL}/api/${type}`, { withCredentials: true }); toast.success(`All ${type} deleted`); fetchData(); }
        catch { toast.error('Failed to delete'); }
    };

    const totalDiscounts = discounts.reduce((s, d) => s + (d.amount || 0), 0);
    const totalDebts = debts.reduce((s, d) => s + (d.amount || 0), 0);

    if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div></div></DashboardLayout>;

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="finance-page">
                <div>
                    <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Finance</h1>
                    <p className="text-[#4B5563]">Manage discounts and debts</p>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 bg-[#F3F4F6] p-1 rounded-lg w-fit">
                    {[{ id: 'discounts', label: 'Discounts', icon: Percent, count: discounts.length },
                      { id: 'debts', label: 'Debts', icon: Receipt, count: debts.length }
                    ].map(tab => (
                        <button key={tab.id} onClick={() => { setActiveTab(tab.id); setUploadResult(null); }} data-testid={`tab-${tab.id}`}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-white text-[#064E3B] shadow-sm' : 'text-[#4B5563] hover:text-[#1F2937]'}`}>
                            <tab.icon className="h-4 w-4" />{tab.label} ({tab.count})
                        </button>
                    ))}
                </div>

                {/* Discounts Tab */}
                {activeTab === 'discounts' && (
                    <div className="space-y-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <Badge variant="secondary" className="text-sm">Total: {'\u20AC'}{totalDiscounts.toFixed(2)}</Badge>
                            <div>
                                <input ref={discountFileRef} type="file" accept=".xlsx,.xls" onChange={(e) => handleUpload('discounts', e)} className="hidden" id="discount-upload" />
                                <Button variant="outline" onClick={() => discountFileRef.current?.click()} disabled={uploading} data-testid="upload-discounts-btn">
                                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}Upload Excel
                                </Button>
                            </div>
                            <Button variant="outline" className="text-[#EF4444] border-[#EF4444]" onClick={() => handleDeleteAll('discounts')} data-testid="delete-all-discounts" style={{display: user?.role === 'super_admin' ? undefined : 'none'}}><Trash2 className="h-4 w-4 mr-2" />Delete All</Button>
                        </div>

                        <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-3 text-sm text-[#166534]">
                            <p className="font-medium">Excel format: <span className="font-mono">guardian_code, amount</span></p>
                        </div>

                        {uploadResult && (<div className="flex items-center gap-4 text-sm"><div className="flex items-center gap-1"><CheckCircle className="h-4 w-4 text-[#10B981]" />{uploadResult.created} imported</div>{uploadResult.skipped > 0 && <div className="flex items-center gap-1 text-[#F59E0B]"><AlertCircle className="h-4 w-4" />{uploadResult.skipped} skipped</div>}</div>)}

                        <Card className="card-surface"><CardContent className="p-0">
                            {discounts.length > 0 ? (
                                <Table>
                                    <TableHeader><TableRow><TableHead>Guardian Code</TableHead><TableHead>Guardian</TableHead><TableHead className="text-right">Discount Amount</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                                    <TableBody>{discounts.map(d => { const info = getParentInfo(d.parent_id); return (
                                        <TableRow key={d.id}>
                                            <TableCell><Badge variant="outline" className="font-mono">{d.parent_code || info.code}</Badge></TableCell>
                                            <TableCell className="font-medium">{info.name}</TableCell>
                                            <TableCell className="text-right font-semibold text-[#10B981] tabular-nums">{'\u20AC'}{d.amount?.toFixed(2)}</TableCell>
                                            <TableCell className="text-right"><Button size="sm" variant="ghost" className="text-[#EF4444]" onClick={() => handleDeleteOne('discounts', d.id)}><Trash2 className="h-4 w-4" /></Button></TableCell>
                                        </TableRow>
                                    ); })}</TableBody>
                                </Table>
                            ) : <div className="p-8 text-center text-[#4B5563]">No discounts. Upload an Excel file to add.</div>}
                        </CardContent></Card>
                    </div>
                )}

                {/* Debts Tab */}
                {activeTab === 'debts' && (
                    <div className="space-y-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <Badge variant="secondary" className="text-sm">Total: {'\u20AC'}{totalDebts.toFixed(2)}</Badge>
                            <div>
                                <input ref={debtFileRef} type="file" accept=".xlsx,.xls" onChange={(e) => handleUpload('debts', e)} className="hidden" id="debt-upload" />
                                <Button variant="outline" onClick={() => debtFileRef.current?.click()} disabled={uploading} data-testid="upload-debts-btn">
                                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}Upload Excel
                                </Button>
                            </div>
                            <Button variant="outline" className="text-[#EF4444] border-[#EF4444]" onClick={() => handleDeleteAll('debts')} data-testid="delete-all-debts" style={{display: user?.role === 'super_admin' ? undefined : 'none'}}><Trash2 className="h-4 w-4 mr-2" />Delete All</Button>
                        </div>

                        <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-3 text-sm text-[#166534]">
                            <p className="font-medium">Excel format: <span className="font-mono">guardian_code, amount</span></p>
                        </div>

                        {uploadResult && (<div className="flex items-center gap-4 text-sm"><div className="flex items-center gap-1"><CheckCircle className="h-4 w-4 text-[#10B981]" />{uploadResult.created} imported</div>{uploadResult.skipped > 0 && <div className="flex items-center gap-1 text-[#F59E0B]"><AlertCircle className="h-4 w-4" />{uploadResult.skipped} skipped</div>}</div>)}

                        <Card className="card-surface"><CardContent className="p-0">
                            {debts.length > 0 ? (
                                <Table>
                                    <TableHeader><TableRow><TableHead>Guardian Code</TableHead><TableHead>Guardian</TableHead><TableHead className="text-right">Debt Amount</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                                    <TableBody>{debts.map(d => { const info = getParentInfo(d.parent_id); return (
                                        <TableRow key={d.id}>
                                            <TableCell><Badge variant="outline" className="font-mono">{d.parent_code || info.code}</Badge></TableCell>
                                            <TableCell className="font-medium">{info.name}</TableCell>
                                            <TableCell className="text-right font-semibold text-[#EF4444] tabular-nums">{'\u20AC'}{d.amount?.toFixed(2)}</TableCell>
                                            <TableCell className="text-right"><Button size="sm" variant="ghost" className="text-[#EF4444]" onClick={() => handleDeleteOne('debts', d.id)}><Trash2 className="h-4 w-4" /></Button></TableCell>
                                        </TableRow>
                                    ); })}</TableBody>
                                </Table>
                            ) : <div className="p-8 text-center text-[#4B5563]">No debts. Upload an Excel file to add.</div>}
                        </CardContent></Card>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default Finance;
